import React, { useContext, useEffect, useRef, useState } from 'react'
import {
  Button,
  Card,
  CardActions,
  CardContent,
  TextField,
  ToggleButton,
  Typography
} from '@mui/material'
import CommentHeader from './CommentHeader'
import SubmitButton from '../../../commonUIComponents/SubmitButton'
import { FormProvider, useForm } from 'react-hook-form'
import commentService from '../../../../services/commentService'
import { enqueueSnackbar } from 'notistack'
import { UserInfoContext } from '../../../contexts/UserInfoContext'
import KeyboardArrowUpOutlinedIcon from '@mui/icons-material/KeyboardArrowUpOutlined'
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined'
import voteService from '../../../../services/voteService'
import VoteToggleButtonGroup from '../../../commonUIComponents/VoteToggleButtonGroup'

const Comment = ({ authToken, comment, highlighted, postId, updateCommentVote, submitOnSuccess }) => {
  const { userInfo } = useContext(UserInfoContext)
  const { id } = comment
  const [isEditingComment, setIsEditingComment] = React.useState(false)
  const [vote, setVote] = useState(null)
  const highlightRef = useRef(null)
  const methods = useForm({
    defaultValues: {
      body: comment?.body
    }
  })
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = methods

  const editCommentCancelHandler = () => {
    setIsEditingComment(false)
    methods.reset()
  }

  const editCommentSubmitHandler = values => {
    values.userId = userInfo.id
    return commentService.updateCommentsById(postId, comment.id, values)
      .then(setIsEditingComment(false))
      .then(submitOnSuccess)
      .then(res => {
        enqueueSnackbar('Successfully updated your comment!', { variant: 'success' })
      })
      .catch(err => enqueueSnackbar(err, { variant: 'error' }))
  }

  const onEditComment = () => {
    setIsEditingComment(true)
  }

  const toggleOnChangeHandler = (event, value) => {
    setVote(value)
    voteService.updateCommentVote(id, value)
      .then(res => {
        updateCommentVote(id, res.myVote, res.votes)
      })
  }

  useEffect(() => {
    highlightRef.current?.scrollIntoView({
      behavior: 'smooth'
    })
    if (comment.myVote === -1) {
      setVote('dislike')
    } else if (comment.myVote === 0) {
      setVote(null)
    } else if (comment.myVote === 1) {
      setVote('like')
    }
  }, [])

  return (
    <>
      {comment
        ? (
          <Card elevation={highlighted ? 3 : 0} ref={highlightRef}>
            <CommentHeader authToken={authToken} comment={comment} postId={postId} onEditComment={onEditComment} submitOnSuccess={submitOnSuccess} />
            <CardContent sx={{ textDecoration: 'none', paddingTop: '0.5rem', paddingLeft: '2rem' }}>
              {!isEditingComment
                ? (
                  <Typography variant="body2" component="div" sx={{ paddingLeft: '0.5rem' }}>
                    {comment.body}
                  </Typography>
                  )
                : (
                  <FormProvider {...methods}>
                    <form onSubmit={handleSubmit(editCommentSubmitHandler)}>
                      <TextField
                        {...register('body', { required: { value: true, message: 'Add a comment' } })}
                        fullWidth
                        multiline
                        InputProps={{
                          endAdornment:
                            <>
                              <Button onClick={editCommentCancelHandler}>Cancel</Button>
                              <SubmitButton buttonLabel="Comment" loadingLabel="Commenting"/>
                            </>
                        }}
                        sx={{ marginBottom: '1.5rem' }}
                      />
                    </form>
                  </FormProvider>
                  )
              }
            </CardContent>
            <CardActions disableSpacing sx={{ mt: '-1rem', border: 'none' }}>
              <VoteToggleButtonGroup
                value={vote}
                exclusive
                onChange={toggleOnChangeHandler}
                sx={{ ml: '1.5rem' }}
                disabled = {!authToken}
              >
                <ToggleButton value="like" aria-label="like">
                  <KeyboardArrowUpOutlinedIcon fontSize="small" />
                </ToggleButton>
                <ToggleButton value="" aria-label="voteCount" disabled>
                  <Typography variant="subtitle2">{comment.votes ? comment.votes : 0}</Typography>
                </ToggleButton>
                <ToggleButton value="dislike" aria-label="dislike">
                  <KeyboardArrowDownOutlinedIcon fontSize="small" />
                </ToggleButton>
              </VoteToggleButtonGroup>
            </CardActions>
          </Card>
          )
        : (
          <></>
          )
      }
    </>
  )
}

export default Comment
